/**
 * @fileoverview Auth API Service
 * @description API service for authentication endpoints
 * @module @xtream/frontend/services/api/auth
 *
 * Endpoints:
 * - POST /auth/login - User login
 * - POST /auth/logout - User logout
 * - POST /auth/refresh - Refresh tokens
 * - GET /auth/me - Get current user
 */

import { apiClient, ApiError } from './client';
import type { IUser, ILoginCredentials, ILoginResponse, ITokenPair } from '@xtream/shared-types';
import { mockUsers } from '@xtream/shared-utils';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Whether to use mock data (for development without backend)
 *
 * Logic:
 * - In production: always use real API (unless VITE_USE_MOCK_API=true)
 * - In development: use real API by default (can enable mock with VITE_USE_MOCK_API=true)
 *
 * FASE 14: Changed default to use real API in development
 * to enable full integration with the backend.
 */
const USE_MOCK = import.meta.env['VITE_USE_MOCK_API'] === 'true';

// Mock delay to simulate network latency
const MOCK_DELAY = 500;

// ═══════════════════════════════════════════════════════════════════════════
// MOCK HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Simulate network delay
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate mock tokens
 */
function generateMockTokens(): ITokenPair {
  return {
    accessToken: `mock_access_${Date.now()}_${Math.random().toString(36).substring(7)}`,
    refreshToken: `mock_refresh_${Date.now()}_${Math.random().toString(36).substring(7)}`,
    expiresIn: 3600, // 1 hour
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// AUTH API SERVICE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Auth API service
 */
export const authApi = {
  /**
   * Login with username and password
   * @param credentials - Login credentials
   * @returns Login response with user and tokens
   */
  async login(credentials: ILoginCredentials): Promise<ILoginResponse> {
    if (USE_MOCK) {
      await delay(MOCK_DELAY);

      // Find mock user
      const user = mockUsers.find((u) => u.username === credentials.username && u.isActive);

      if (!user) {
        throw new ApiError('Invalid username or password', 'UNAUTHORIZED', 401);
      }

      // In mock mode, accept any password for valid users
      // In production, this would validate against the password hash
      if (credentials.password.length < 3) {
        throw new ApiError('Invalid username or password', 'UNAUTHORIZED', 401);
      }

      return {
        user,
        tokens: generateMockTokens(),
      };
    }

    const response = await apiClient.post<ILoginResponse>('/auth/login', credentials, {
      skipAuth: true,
    });

    if (!response.success || !response.data) {
      throw new ApiError(
        response.error?.message || 'Login failed',
        response.error?.code || 'LOGIN_FAILED',
        401,
      );
    }

    return response.data;
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    if (USE_MOCK) {
      await delay(MOCK_DELAY / 2);
      return;
    }

    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore logout errors - we'll clear local state regardless
    }
  },

  /**
   * Refresh access token
   * @param refreshToken - Current refresh token
   * @returns New token pair
   */
  async refresh(refreshToken: string): Promise<ITokenPair> {
    if (USE_MOCK) {
      await delay(MOCK_DELAY / 2);
      return generateMockTokens();
    }

    const response = await apiClient.post<ITokenPair>(
      '/auth/refresh',
      { refreshToken },
      { skipAuth: true },
    );

    if (!response.success || !response.data) {
      throw new ApiError(
        response.error?.message || 'Token refresh failed',
        response.error?.code || 'REFRESH_FAILED',
        401,
      );
    }

    return response.data;
  },

  /**
   * Get current authenticated user
   * @returns Current user data
   */
  async me(): Promise<IUser> {
    if (USE_MOCK) {
      await delay(MOCK_DELAY / 2);
      // Return the admin user as the "current" user
      const user = mockUsers.find((u) => u.username === 'admin');
      if (!user) {
        throw new ApiError('User not found', 'NOT_FOUND', 404);
      }
      return user;
    }

    const response = await apiClient.get<IUser>('/auth/me');

    if (!response.success || !response.data) {
      throw new ApiError(
        response.error?.message || 'Failed to get user',
        response.error?.code || 'GET_USER_FAILED',
        401,
      );
    }

    return response.data;
  },

  /**
   * Check if username is available
   * @param username - Username to check
   * @returns Whether the username is available
   */
  async checkUsername(username: string): Promise<boolean> {
    if (USE_MOCK) {
      await delay(MOCK_DELAY / 4);
      return !mockUsers.some((u) => u.username === username);
    }

    const response = await apiClient.get<{ available: boolean }>(
      `/auth/check-username/${username}`,
      { skipAuth: true },
    );

    return response.data?.available ?? true;
  },
};

export default authApi;
